import type { AnyAction } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import { handleResponseError } from '@/core/utils/handleResponseError';
import type {
	TestQualityData,
	TestQualityState,
	TestQualityWire,
} from '@/store/types/testQualityTypes';
import { normalizeTestQuality } from '@/store/types/testQualityTypes';

const initialState: TestQualityState = {
	testId: null,
	data: null,
	isFetching: false,
	error: null,
	isNetworkError: false,
	isNotFound: false,
};

interface FetchErrorPayload {
	errorMessage: string;
	isNetworkError: boolean;
	statusCode: number | null;
}

const testQualitySlice = createSlice({
	name: 'testQuality',
	initialState,
	reducers: {
		fetchQualityStart(state, action: PayloadAction<number>) {
			state.testId = action.payload;
			state.data = null;
			state.isFetching = true;
			state.error = null;
			state.isNetworkError = false;
			state.isNotFound = false;
		},
		setQualityLoaded(state, action: PayloadAction<TestQualityData>) {
			state.data = action.payload;
			state.isFetching = false;
		},
		fetchQualityError(state, action: PayloadAction<FetchErrorPayload>) {
			state.isFetching = false;
			if (action.payload.statusCode === 404) {
				state.isNotFound = true;
				return;
			}
			state.error = action.payload.errorMessage;
			state.isNetworkError = action.payload.isNetworkError;
		},
	},
});

export const { fetchQualityStart, setQualityLoaded, fetchQualityError } = testQualitySlice.actions;

/** Повторное открытие того же теста не перезапрашивает; `force` — для «Перезагрузить» после ошибки. */
export const fetchTestQuality = (testId: number, force = false) => async (
	dispatch: Dispatch<AnyAction>,
	getState: () => { testQuality: TestQualityState },
) => {
	const current = getState().testQuality;
	if (!force && current.testId === testId && (current.data || current.isFetching || current.isNotFound)) {
		return;
	}

	dispatch(fetchQualityStart(testId));
	try {
		const { data } = await apiUrl.get<{ data: TestQualityWire }>(API_ENDPOINTS.testQuality(testId));
		const locale = window.App?.locale ?? 'ru';
		dispatch(setQualityLoaded(normalizeTestQuality(data.data, locale)));
	} catch (error) {
		handleResponseError(
			error,
			(errorMessage: string, isNetworkError: boolean, statusCode: number | null) => {
				dispatch(fetchQualityError({ errorMessage, isNetworkError, statusCode }));
			},
		);
	}
};

export default testQualitySlice.reducer;
export { testQualitySlice };
